import React from 'react';
import {SvgIcon} from '@/core/components/svgIcon/SvgIcon';
import {useUnit} from 'effector-react';
import {$sectorsTerms, $programmeTerms, $expenditureTerms} from '@/core/store/terms';
import './SelectedFilters.scss';

export interface SelectedFiltersProps {
  filters: {
    fromDate: string | Date | null;
    toDate: string | Date | null;
    programmes: string[];
    sectors: string[];
    expenditures: string[];
  };
  onRemoveFilter: (category: string, value: string) => void;
  onClearAll: () => void;
  type: string;
}

export const SelectedFilters: React.FC<SelectedFiltersProps> = ({filters, onRemoveFilter, onClearAll, type}) => {
  // Get terms data from store
  const [sectorsTerms, programmeTerms, expenditureTerms] = useUnit([$sectorsTerms, $programmeTerms, $expenditureTerms]);

  // Helper function to check if a value is a numeric string (ID)
  const isNumericString = (value: string): boolean => {
    return /^\d+$/.test(value);
  };

  // Helper function to get name from ID using terms store
  const getTermNameById = (category: string, termId: string): string => {
    // If it's not a numeric string, return as-is (it's already a name)
    if (!isNumericString(termId)) {
      return termId;
    }

    // It's a numeric string, so look up the name in terms store
    switch (category) {
      case 'sectors':
        if (!sectorsTerms.data || sectorsTerms.data.length === 0) return termId;
        const sector = sectorsTerms.data.find((term) => term.tid === termId);
        return sector ? sector.name : termId;

      case 'programmes':
        if (!programmeTerms.data || programmeTerms.data.length === 0) return termId;
        const programme = programmeTerms.data.find((term) => term.tid === termId);
        return programme ? programme.name : termId;

      case 'expenditures':
        if (!expenditureTerms.data || expenditureTerms.data.length === 0) return termId;
        const expenditure = expenditureTerms.data.find((term) => term.tid === termId);
        return expenditure ? expenditure.name : termId;

      default:
        return termId; // Return as-is for date filters or unknown categories
    }
  };

  // Get all active filters
  const activeFilters: Array<{category: string; value: string; label: string; displayValue: string}> = [];

  // Helper function to format dates for display
  const formatDateForDisplay = (date: string | Date | null): string => {
    if (!date) return '';
    if (typeof date === 'string') return date;
    return date.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit', year: 'numeric'});
  };

  // Helper function to get date string value for removal
  const getDateStringValue = (date: string | Date | null): string => {
    if (!date) return '';
    if (typeof date === 'string') return date;
    return date.toISOString().split('T')[0];
  };

  // Handle date filters
  if (filters.fromDate) {
    activeFilters.push({
      category: 'fromDate',
      value: getDateStringValue(filters.fromDate),
      label: 'From Date',
      displayValue: formatDateForDisplay(filters.fromDate),
    });
  }

  if (filters.toDate) {
    activeFilters.push({
      category: 'toDate',
      value: getDateStringValue(filters.toDate),
      label: 'To Date',
      displayValue: formatDateForDisplay(filters.toDate),
    });
  }

  // Handle array filters
  Object.entries(filters).forEach(([category, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      values.forEach((value) => {
        activeFilters.push({
          category,
          value,
          label: getCategoryLabel(category),
          displayValue: getTermNameById(category, value), // Map ID to name
        });
      });
    }
  });

  // Helper function to get human-readable category names
  function getCategoryLabel(category: string): string {
    const categoryLabels: Record<string, string> = {
      fromDate: 'From',
      toDate: 'To',
      programmes: 'Programme',
      sectors: 'Sector',
      expenditures: 'Budget',
    };
    return categoryLabels[category] || category;
  }

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="selected-filters">
      <div className="selected-filters-header">
        {/* <span className="filters-count"> */}
        {/* {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''} applied */}
        {/* </span> */}
        {/* <button className="clear-all-btn" onClick={onClearAll}>
          <SvgIcon icon="icon-close" />
          Clear all
        </button> */}
      </div>

      <div className="filters-list">
        {activeFilters.map((filter, index) => (
          <div key={`${filter.category}-${filter.value}-${index}`} className="filter-tag">
            <span className="filter-category">{filter.label}:</span>
            <span className="filter-value">{filter.displayValue}</span>
            <button
              className="remove-filter-btn"
              onClick={() => onRemoveFilter(filter.category, filter.value)}
              aria-label={`Remove ${filter.displayValue} filter`}
            >
              <SvgIcon icon="icon-close" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
